import { useTheme } from "@mui/material";
import { SyntheticEvent, useContext, useEffect, useState } from "react";
import { AppContext } from "../../context/appContext";
import { MapPlotData, RegionsTooltip } from "../../types/type";
import Box from "../common/Box";
import Loader from "../common/Loader";
import { Tab, TabPanel, Tabs } from "../common/Tabs";
import Typography from "../common/Typography";
import WorldMap, { CountryData } from "../common/WorldMap";
import GeoDataStoreCard from "./GeoDataStoreCard";
import { regions } from "../maps/aws_regions";
import { gcpRegions } from "../maps/gcp_regions";
import { isContainsValue } from "../../utils/commonUtils";
import { DASHBOARD_DATA_VIEW } from "../constants/constants";
import LegendWithColor from "../common/LegendWithColor";
import { frameworkGeoData } from "../../mock-data/llmPromptsData";

const GeoDistributionCard = () => {
  const [activeTab, setActiveTab] = useState("inventory-view");
  const {
    aiSummary,
    dashboardViewLense,
    geoDistribution,
    isFetchingGeoDistribution,
    getGeoDistributionData,
    overviewAllRegions,
    isFetchingAllRegionsOverview,
    getSensitiveData,
    overviewForSelectedRegion,
    isFetchingSelectedRegionOverview,
    getRegionsOverview,
  } = useContext(AppContext);

  const [geoView, setGeoView] = useState<string>("llms");
  const [activeRegion, setActiveRegion] = useState<{
    data: [];
    region: string;
    regionCode: string;
  }>({
    data: [],
    region: "All Regions",
    regionCode: "all",
  });
  const [geoData, setGeoData] = useState<
    Array<{ type: string; data: Array<MapPlotData> }>
  >([{ type: "", data: [] }]);
  const [tooltipData, setTooltipData] = useState<RegionsTooltip>({
    impacted: 0,
    cloud: "",
    data: [],
  });
  const isFrameworkView = geoView === "frameworks";
  const [overviewAll, setOverviewAll] = useState([]);
  const [overviewRegionSpecific, setOverviewRegionSpecific] = useState([]);
  const [impactedDataStore, setImpactedDataStore] = useState(0);
  const [totalDataStore, setTotalDataStore] = useState(0);
  const dataView = dashboardViewLense === DASHBOARD_DATA_VIEW;

  const theme = useTheme();
  const styles = {
    geoCard: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(3),
      padding: theme.spacing(2),
      backgroundColor: theme.palette.surface10.main,
      borderRadius: theme.spacing(0.5),
      position: "relative",
    },
    cardTitle: {
      fontSize: "13px",
      fontWeight: theme.typography.fontWeightMedium,
    },
    cardContent: {
      width: "100%",
      display: "grid",
      gridTemplateColumns: "65% 35%",
    },
    mapContainer: {
      width: "100%",
    },
    mapTabs: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
    },
    mapLegends: {
      width: "fit-content",
    },
    cardHeading: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
    },
  };

  const cardTabs = [
    {
      value: "inventory-view",
      label: "Inventory View",
      color: theme.palette.lightMagenta.main,
      stroke: theme.palette.purpleStroke.main,
    },
    //{
    //  value: 'risk-view',
    //  label: 'Risk View',
    //  color: theme.palette.critical.main,
    //  stroke: theme.palette.redStroke.main,
    //},
  ];

  useEffect(() => {
    if (dataView) {
      getGeoDistributionData();
      getSensitiveData();
    } else {
      getGeoDistributionData({ type: "llm" });
      getSensitiveData({ type: "llm" });
    }
  }, [dashboardViewLense]);

  const getImpactedDSCount = () => {
    return overviewAllRegions?.sensitive_datastore;
    //return activeRegion?.regionCode === 'all'
    //  ? overviewAllRegions?.sensitive_datastore || 0
    //  : overviewRegionSpecific?.reduce(
    //      (
    //        acc: number,
    //        curr: {
    //          dataType: string;
    //          numberOfRecords: number;
    //          totalValue: number;
    //          impacted: number;
    //        }
    //      ) => acc + curr.impacted,
    //      0
    //    );
  };

  const formatDataForMap = (data) => {
    return data.map((dataItem) => {
      const regionData =
        !dataItem?.cloud || dataItem?.cloud === "aws"
          ? regions.find((region) => region.code === dataItem?.region)
          : gcpRegions.find((region) => region.code === dataItem?.region);
      return {
        type: "Feature",
        properties: {
          id: dataItem.region,
          region: dataItem?.region,
          name: regionData?.name,
          impacted: dataView
            ? dataItem?.dsTypes[0]?.count
            : !isFrameworkView
            ? dataItem?.dsTypes?.find((data) => data?.type === "LLM")?.count
            : dataItem?.datastores,
          hasDataResidencyViolation:
            dataView || isFrameworkView
              ? dataItem?.hasDataResidencyViolation
              : dataItem?.region === "us-east-1",
          hasDataStoreRisk: dataItem?.hasDataStoreRisk,
          sensitiveData: dataItem?.sensitiveData || dataItem?.sensitive_data,
          cloud: dataItem?.cloud,
        },
        geometry: {
          type: "Circle",
          coordinates: [regionData?.longitude, regionData?.latitude],
        },
      };
    });
  };

  useEffect(() => {
    if (Object.values(geoDistribution).length > 0) {
      let formattedData;
      if (isFrameworkView) {
        formattedData = formatDataForMap(frameworkGeoData);
      } else {
        formattedData = formatDataForMap(geoDistribution);
      }
      const inventoryView = formattedData;
      const riskView = formattedData.filter(
        (data) => data.properties.hasDataStoreRisk
      );
      setGeoData([
        { type: "inventory-view", data: inventoryView },
        {
          type: "risk-view",
          data: riskView,
        },
      ]);
    }
  }, [geoDistribution, geoView]);

  const totalLLMs = isContainsValue(aiSummary?.llm_ds_type)
    ? Object.values(aiSummary?.llm_ds_type)?.reduce(
        (acc: number, curr: any) => {
          return acc + curr;
        },
        0
      )
    : 0;

  useEffect(() => {
    if (isContainsValue(overviewAllRegions) || isFrameworkView) {
      let cardData;
      if (isFrameworkView) {
        cardData = frameworkGeoData[0];
      } else {
        cardData = overviewAllRegions;
      }
      const totalRecords = cardData?.sensitive_data?.reduce(
        (acc: number, curr) => acc + (curr?.count || curr?.records),
        0
      );
      const overviewAll = cardData.sensitive_data?.map((data) => ({
        dataType: data?.tag,
        numberOfRecords: data?.count || data?.records,
        impacted: data?.datastores,
        totalValue: totalRecords,
      }));
      setOverviewAll(overviewAll);
      setImpactedDataStore(cardData?.sensitive_datastore);
      setTotalDataStore(overviewAllRegions?.total_datastore);
    }
  }, [overviewAllRegions, geoData]);

  useEffect(() => {
    if (activeRegion?.regionCode !== "all")
      getRegionsOverview({
        region: activeRegion?.regionCode,
        type: dataView ? "" : "llm",
      });
    else {
      if (!isFrameworkView) {
        setImpactedDataStore(overviewAllRegions?.sensitive_datastore);
        setTotalDataStore(overviewAllRegions?.total_datastore);
      } else {
        setImpactedDataStore(frameworkGeoData[0]?.sensitive_datastore);
        setTotalDataStore(0);
      }
    }
  }, [activeRegion]);

  useEffect(() => {
    if (Object.values(overviewForSelectedRegion)?.length > 0) {
      let cardData;
      if (isFrameworkView) {
        cardData = frameworkGeoData[0];
      } else {
        cardData = overviewForSelectedRegion;
      }
      const totalRecords = cardData?.sensitive_data?.reduce(
        (acc: number, curr) => acc + (curr?.count || curr?.records),
        0
      );
      setOverviewRegionSpecific(
        cardData?.sensitive_data?.map((data) => ({
          dataType: data?.tag,
          numberOfRecords: data?.count || data?.records,
          impacted: data?.datastores,
          totalValue: totalRecords,
        }))
      );
      setImpactedDataStore(cardData?.sensitive_datastore);
      setTotalDataStore(overviewForSelectedRegion?.total_datastore);
    }
  }, [overviewForSelectedRegion]);

  const handleTabChange = (event: Event | SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
    setActiveRegion({ data: [], region: "All Regions", regionCode: "all" });
  };

  const handleMouseOverTooltip = (cloud, data, impacted) => {
    if (tooltipData?.data?.length === 0) {
      const recordsForTooltip =
        data?.map(({ tag, records }) => ({
          dataType: tag,
          records: records,
        })) || [];
      setTooltipData({ impacted: impacted, data: recordsForTooltip, cloud });
    }
  };

  const tabData = [
    {
      value: "llms",
      label: "LLMs",
    },
    {
      value: "frameworks",
      label: "Frameworks",
    },
  ];

  return (
    <Box sx={styles.geoCard}>
      {isFetchingGeoDistribution ? (
        <Loader height="531px" />
      ) : (
        <>
          <Box sx={styles.cardHeading}>
            <Typography
              color={theme.palette.surface30.main}
              sx={styles.cardTitle}
            >
              {dataView
                ? "Geo Distribution of Impacted Data Stores by Sensitive Data Type"
                : "Geo distribution"}
            </Typography>
            <Tabs
              value={geoView}
              onChange={(event, newValue) => {
                setGeoView(newValue);
              }}
            >
              {tabData?.map((item) => (
                <Tab value={item.value} label={item.label} key={item.value} />
              ))}
            </Tabs>
          </Box>
          <Box sx={styles.cardContent}>
            <Box sx={styles.mapTabs}>
              <Box sx={{ display: dataView ? "" : "none" }}>
                <Tabs value={activeTab} onChange={handleTabChange}>
                  {cardTabs.map((tab: object, index: number) => (
                    <Tab {...tab} key={index} />
                  ))}
                </Tabs>
              </Box>
              {/*{!dataView && (
                <Box sx={styles.mapLegends}>
                  <LegendWithColor
                    label="LLMs With Sensitive Data"
                    color={theme.palette.lightMagenta.main}
                  />
                </Box>
              )}*/}
              <WorldMap
                countriesToPlot={
                  geoData.find((data) => data.type === activeTab)?.data || []
                }
                activeTabData={
                  cardTabs.find((tab) => tab.value === activeTab) || cardTabs[0]
                }
                activeRegion={activeRegion}
                dataView={dataView}
                setActiveRegion={setActiveRegion}
                handleMouseOverTooltip={handleMouseOverTooltip}
                tooltipData={tooltipData || []}
                clearTooltipData={() =>
                  setTooltipData({
                    impacted: 0,
                    cloud: "",
                    data: [],
                  })
                }
                isFrameworkView={isFrameworkView}
              />
            </Box>
            <GeoDataStoreCard
              barColor={
                cardTabs.find((tab) => tab?.value === activeTab)?.color || ""
              }
              impactedDS={impactedDataStore}
              totalDS={totalDataStore}
              activeRegion={activeRegion}
              dataDistribution={
                activeRegion.regionCode === "all"
                  ? overviewAll
                  : overviewRegionSpecific
              }
              isFetchingData={
                isFetchingAllRegionsOverview || isFetchingSelectedRegionOverview
              }
              isFrameworkView={isFrameworkView}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default GeoDistributionCard;
